import React, { useState, useEffect, useCallback } from "react";

// recoil
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials, routeInfo } from "recoilState/GlobalState";

//Services
import { horarioRangoGet } from "services/services-mia/miakey";

// utils
import XGrid from "components/UI/Grid/XGrid";
import { HeaderButton, Buttons } from "components";
import { Add, Cached, DeleteOutline, EditOutlined } from "@mui/icons-material";

import Modal from "components/UI/Modal/Modal";
import { DeleteModal, InsertModal, UpdateModal } from "./Actions";

// consts
import { rangos as titles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";
import { Routes } from "consts/Routes-App";

// styles
import s from "styles/pages/Pages.module.scss"


export default function HorarioRangos({
  horarioId,
  service,
  onUpdate,
}) {
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} })

  const credentials = useRecoilValue(userCredentials);
  const setRoute = useSetRecoilState(routeInfo);

  const updateList = useCallback(async () => {
    setIsLoading(true);
    setRows([]);
    const { result } = await horarioRangoGet({ gateHorarioId: horarioId }, credentials);

    // Agrupar por entrada+salida
    const grupos = {};
    for (const item of result) {
      const key = `${item.gateHorarioRangoEntrada}|${item.gateHorarioRangoSalida}`;
      if (!grupos[key]) {
        grupos[key] = {
          gateHorarioId: item.gateHorarioId,
          gateHorarioRangoId: item.gateHorarioRangoId,
          gateHorarioRangoVersion: item.gateHorarioRangoVersion,
          gateHorarioRangoEntrada: item.gateHorarioRangoEntrada,
          gateHorarioRangoSalida: item.gateHorarioRangoSalida,
          dias: [],
        };
      }
      grupos[key].dias.push(item.gateHorarioRangoDia);
    }

    setRows(Object.entries(grupos).sort(([, a], [, b]) => a.gateHorarioRangoEntrada.localeCompare(b.gateHorarioRangoEntrada)).map(([key, grupo]) => {
      const entrada = grupo.gateHorarioRangoEntrada.substring(0, 5);
      const salida = grupo.gateHorarioRangoSalida.substring(0, 5);
      return {
        id: key,
        gateHorarioId: grupo.gateHorarioId,
        gateHorarioRangoId: grupo.gateHorarioRangoId,
        gateHorarioRangoVersion: grupo.gateHorarioRangoVersion,
        gateHorarioRangoEntrada: grupo.gateHorarioRangoEntrada,
        gateHorarioRangoSalida: grupo.gateHorarioRangoSalida,
        dias: grupo.dias,
        [titles[0]]: entrada,
        [titles[1]]: salida,
        [titles[2]]: grupo.dias.includes("LU") ? "✓" : "",
        [titles[3]]: grupo.dias.includes("MA") ? "✓" : "",
        [titles[4]]: grupo.dias.includes("MI") ? "✓" : "",
        [titles[5]]: grupo.dias.includes("JU") ? "✓" : "",
        [titles[6]]: grupo.dias.includes("VI") ? "✓" : "",
        [titles[7]]: grupo.dias.includes("SA") ? "✓" : "",
        [titles[8]]: grupo.dias.includes("DO") ? "✓" : "",
      };
    }));
    setIsLoading(false);
    onUpdate?.();
  }, [credentials, horarioId, onUpdate]);

  useEffect(() => {
    setRoute(Routes.horarios)
  }, [setRoute])

  useEffect(() => {
    updateList()
  }, [service, updateList]);

  const handleClick = ({ procede, title }) => {
    setModalDetails({ title, procede, isOpen: true });
  };

  const closeModal = () => {
    setModalDetails({ isOpen: false });
  };

  return (
    <main className={s.main}>
      <Modal onClose={closeModal} open={modalDetails.isOpen} title={modalDetails.title}>
        {modalDetails.procede === ACTIONS.UPDATE && (
          <UpdateModal
            horarioRango={rowSelected.row}
            updateList={updateList}
            closeModal={closeModal}
            onSuccess={() => setRowSelected({ status: false, row: {} })}
          />
        )}
        {modalDetails.procede === ACTIONS.DELETE && (
          <DeleteModal
            horarioRango={rowSelected.row}
            updateList={updateList}
            closeModal={closeModal}
            onSuccess={() => setRowSelected({ status: false, row: {} })}
          />
        )}
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            horarioId={horarioId}
            updateList={updateList}
            closeModal={closeModal}
          />
        )}
      </Modal>
      <div className={s.actions}>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={updateList}
            disabled={rowSelected.row.id}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Agregar Rango"}
            handleClick={handleClick}
            data={{
              title: "Agregar Rango Horario",
              procede: ACTIONS.INSERT
            }}
            disabled={rowSelected.row.id || service?.servicioPermiso !== 'E'}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Editar Rango"}
            handleClick={handleClick}
            data={{
              title: "Editar Rango Horario",
              procede: ACTIONS.UPDATE
            }}
            disabled={!rowSelected.row.id || service?.servicioPermiso !== 'E'}
          >
            <EditOutlined />
          </HeaderButton>

          <HeaderButton
            text={"Eliminar Rango"}
            handleClick={handleClick}
            data={{
              title: "Eliminar Rango Horario",
              procede: ACTIONS.DELETE,
            }}
            disabled={!rowSelected.row.id || !service.servicioPermiso === 'E'}
          >
            <DeleteOutline />
          </HeaderButton>
        </Buttons>
      </div>
      <div className={s.container}>
        <XGrid
          rows={rows}
          titles={titles}
          loading={isLoading}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>
    </main>
  );
}
